import { inject, Injectable } from '@angular/core';
import { catchError, EmptyError, forkJoin, map, Observable, of, Subject, switchMap, throwError, TimeoutError } from 'rxjs';
import { WorkflowBadgeStatus, WorkflowItem } from '#models';
import { DossierService, DossierSummary } from '../dossier/dossier.service';
import { CaseStateView } from './generated/models/case-state-view';
import { QueueItem } from './generated/models/queue-item';
import { WorkflowGraph } from './generated/models/workflow-graph';
import { AuditTrailResponse } from './generated/models/audit-trail-response';
import { usesRealWorkflowEngine, WORKFLOW_ENGINE_CONFIGURATION } from './provide-workflow-engine-gateway';
import { WORKFLOW_ENGINE_GATEWAY } from './workflow-engine-gateway';

const PAGE_SIZE = 50;
const QUEUE_PAGE_SIZE = 100;

const EXCEPTION_TYPE_BY_USE_CASE: Record<string, string> = {
  UC1: 'Export Volume Deviation',
  UC2: 'Well Exception Correlation',
};

/**
 * `failed` is true when the engine cases could not be listed, so the inbox can say so instead of looking empty.
 * `links` maps an inbox case id (`case-{agentCaseId}`) to the engine case its Dossier opened, only for a case the
 * engine returned (queued or read), so an inbox case shows engine data only when there is some.
 */
export interface EngineInboxResult {
  items: WorkflowItem[];
  links: Record<string, string>;
  failed: boolean;
}

interface JoinedCase {
  summary: DossierSummary;
  item: WorkflowItem;
  read: boolean;
  progressFailed?: boolean;
}

export type WorkflowProgress = Pick<WorkflowItem, 'workflowBadge' | 'workflowStatus' | 'workflowStepTitle' | 'workflowOutcome' | 'workflowOwnerOid'>;

export function workflowProgress(
  state: CaseStateView | null,
  queued: QueueItem | undefined,
  graph: WorkflowGraph | null,
  audit: AuditTrailResponse | null,
): WorkflowProgress {
  if (!state && !queued) return {};
  const badge = engineBadge(queued, state);
  if (badge === WorkflowBadgeStatus.Closed) {
    return { workflowBadge: badge, workflowStatus: 'done', workflowStepTitle: null, workflowOutcome: state?.outcome ?? null, workflowOwnerOid: null };
  }
  const stepKey = state?.activeStep?.stepKey ?? queued?.stepKey;
  const node = graph?.nodes.find(entry => entry.id === stepKey);
  const progressed = !!state?.ownerOid || (node !== undefined && node.start !== true) || leftOpeningStep(state)
    || audit?.entries.some(entry => entry.eventKind === 'owner-claimed'
      || (entry.eventKind === 'step-entered' && entry.toStepKey !== stepKey));
  return {
    workflowBadge: badge,
    workflowStatus: progressed ? 'in-progress' : state || node ? 'new' : undefined,
    workflowStepTitle: node?.title?.trim() || null,
    workflowOutcome: null,
    workflowOwnerOid: state ? (state.status === 'open' && state.activeStep ? state.ownerOid : null) : queued ? null : undefined,
  };
}

/**
 * Projects one Dossier onto an inbox row. The Dossier says what the case is about and which facility it
 * belongs to; the engine says where it stands. `queued` is the role-queue entry when the case waits to be
 * claimed; `state` is the engine's read of a case that is not queued, or null when that read failed.
 */
export function toEngineInboxItem(
  summary: DossierSummary,
  queued: QueueItem | undefined,
  state: CaseStateView | null,
): WorkflowItem {
  const caseNumber = queued?.caseNumber ?? state?.caseNumber ?? null;
  const badge = engineBadge(queued, state);
  const exceptionType = EXCEPTION_TYPE_BY_USE_CASE[summary.useCaseCode] ?? summary.useCaseCode;
  const signals = `${summary.signalCount} signal${summary.signalCount === 1 ? '' : 's'}`;

  return {
    id: summary.engineCaseId,
    engineCaseId: summary.engineCaseId,
    title: summary.topCandidateTitle ?? caseNumber ?? 'Case under review',
    badge,
    workflowBadge: queued || state ? badge : undefined,
    workflowOwnerOid: state ? (state.status === 'open' && state.activeStep ? state.ownerOid : null) : queued ? null : undefined,
    asset: summary.facility ?? undefined,
    priority: null,
    description: [caseNumber ?? summary.useCaseCode, signals, summary.facility].filter(Boolean).join(' · '),
    createdDate: state?.openedOnUtc ?? summary.createdDate,
    updatedDate: state?.closedOnUtc ?? queued?.enteredOnUtc ?? state?.openedOnUtc ?? summary.createdDate,
    exceptionReferenceId: caseNumber,
    assignedTo: null,
    status: state?.outcome ?? (badge === WorkflowBadgeStatus.Closed ? 'Closed' : 'Open'),
    identity: {
      title: summary.topCandidateTitle ?? caseNumber ?? '',
      area: '',
      supplementary: summary.facility ?? '',
      stream: '',
      product: '',
      exceptionType,
      useCase: summary.useCaseCode,
      confidenceLevel: 0,
      confidenceStatement: '',
    },
    steps: [],
    comments: [],
  };
}

/** The engine stamps a new case's first step with its opening time; any later step entry is newer. */
function leftOpeningStep(state: CaseStateView | null): boolean {
  const entered = Date.parse(state?.activeStep?.enteredOnUtc ?? '');
  const opened = Date.parse(state?.openedOnUtc ?? '');
  return entered > opened;
}

function engineBadge(queued: QueueItem | undefined, state: CaseStateView | null): WorkflowBadgeStatus {
  if (queued) {
    return WorkflowBadgeStatus.New;
  }
  if (state && state.status !== 'open') {
    return WorkflowBadgeStatus.Closed;
  }
  return state && !state.ownerOid ? WorkflowBadgeStatus.New : WorkflowBadgeStatus.AwaitingDecision;
}

/** Lists the cases the inbox shows from the workflow engine: IO's Dossiers for the facility, joined with the engine on `engineCaseId`. */
@Injectable({ providedIn: 'root' })
export class EngineInboxService {
  private _gateway = inject(WORKFLOW_ENGINE_GATEWAY);
  private _dossiers = inject(DossierService);
  private _configuration = inject(WORKFLOW_ENGINE_CONFIGURATION);
  private _stateRead = new Subject<{ caseId: string; badge: WorkflowBadgeStatus; progress: WorkflowProgress }>();
  readonly stateRead$ = this._stateRead.asObservable();

  /** Keeps inbox indicators current after the open workflow is read, including after commands. */
  reportState(state: CaseStateView, graph: WorkflowGraph, audit: AuditTrailResponse): void {
    this._stateRead.next({ caseId: state.caseId, badge: engineBadge(undefined, state), progress: workflowProgress(state, undefined, graph, audit) });
  }

  /** Lists nothing without a selected facility or a deployed engine, so no Dossier is shown with invented engine state. */
  load(facilityId: string | null): Observable<EngineInboxResult> {
    if (!facilityId || !usesRealWorkflowEngine(this._configuration)) {
      return of({ items: [], links: {}, failed: false });
    }

    return this._listDossiers(facilityId).pipe(
      switchMap(dossiers => (dossiers.length === 0 ? of<JoinedCase[]>([]) : this._join(dossiers))),
      map(joined => ({ items: joined.map(entry => entry.item), links: caseLinks(joined), failed: joined.some(entry => !entry.read || entry.progressFailed) })),
      catchError(() => of<EngineInboxResult>({ items: [], links: {}, failed: true })),
    );
  }

  private _listDossiers(facilityId: string, page = 1): Observable<DossierSummary[]> {
    return this._dossiers.list({ facilityId, page, pageSize: PAGE_SIZE }).pipe(
      switchMap(dossiers => dossiers.length === PAGE_SIZE
        ? this._listDossiers(facilityId, page + 1).pipe(map(next => [...dossiers, ...next]))
        : of(dossiers)),
    );
  }

  private _join(dossiers: DossierSummary[]): Observable<JoinedCase[]> {
    // A queue that cannot be read only costs the shortcut: every case then gets its own state read. An engine that
    // gave no answer at all, or no token to ask with, would make each of those reads wait out the same failure.
    return this._gateway.getQueue({ pageSize: QUEUE_PAGE_SIZE }).pipe(
      map(queue => new Map(queue.items.map(item => [item.caseId, item]))),
      catchError((error: unknown) =>
        error instanceof TimeoutError || error instanceof EmptyError
          ? throwError(() => error)
          : of(new Map<string, QueueItem>()),
      ),
      switchMap(queued => this._caseTypeGraphs(dossiers, queued).pipe(
        switchMap(graphs => forkJoin(dossiers.map(summary => this._joinCase(summary, queued.get(summary.engineCaseId), graphs)))),
      )),
    );
  }

  /** One graph per queued case type tells which queued cases still wait on their first step. */
  private _caseTypeGraphs(dossiers: DossierSummary[], queued: Map<string, QueueItem>): Observable<Record<string, WorkflowGraph | null>> {
    const caseTypes = [...new Set(dossiers.flatMap(summary => queued.get(summary.engineCaseId)?.caseType ?? []))];
    if (caseTypes.length === 0) {
      return of({});
    }
    return forkJoin(Object.fromEntries(caseTypes.map(caseType => [
      caseType,
      this._gateway.getCaseTypeWorkflow(caseType).pipe(catchError(() => of<WorkflowGraph | null>(null))),
    ])));
  }

  /** A queued case costs no read of its own unless its step is missing from its case type's current graph. */
  private _joinCase(summary: DossierSummary, entry: QueueItem | undefined, graphs: Record<string, WorkflowGraph | null>): Observable<JoinedCase> {
    if (!entry) {
      return this._gateway.getCaseState(summary.engineCaseId).pipe(
        catchError(() => of<CaseStateView | null>(null)),
        map(state => joined(summary, undefined, state, null)),
      );
    }
    const graph = graphs[entry.caseType] ?? null;
    // A case pinned to an older class version may wait on a step the current version no longer has.
    const graph$ = graph?.nodes.some(node => node.id === entry.stepKey)
      ? of(graph)
      : this._gateway.getCaseWorkflow(summary.engineCaseId).pipe(catchError(() => of<WorkflowGraph | null>(null)));
    return graph$.pipe(map(pinned => joined(summary, entry, null, pinned)));
  }
}

/** Inbox rows carry no step title: only the case's own pinned graph names its step, and the inbox does not read it. */
function joined(summary: DossierSummary, entry: QueueItem | undefined, state: CaseStateView | null, graph: WorkflowGraph | null): JoinedCase {
  const read = !!entry || !!state;
  const progress = workflowProgress(state, entry, graph, null);
  const item = { ...toEngineInboxItem(summary, entry, state), ...progress, workflowStepTitle: undefined };
  return { summary, item, read, progressFailed: read && !progress.workflowStatus };
}

function caseLinks(joined: JoinedCase[]): Record<string, string> {
  const links: Record<string, string> = {};
  for (const { summary, read } of joined) {
    if (read && summary.agentCaseId != null) {
      links[`case-${summary.agentCaseId}`] = summary.engineCaseId;
    }
  }
  return links;
}
